// frontend-dash/src/pages/MyChildren.js
import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import {
    TeamIcon as Users,
    AddUserIcon as UserPlus,
    UserIcon as User,
    DeleteIcon as Trash,
    CloseIcon as X,
    CheckCircleIcon as CheckCircle,
    AlertCircleIcon as AlertCircle,
    MailIcon as Mail,
    CalendarIcon as Calendar,
} from '../components/Icons';

const MyChildren = () => {
    const { profile, loadingProfile } = useOutletContext();
    const { user, API_URL } = useContext(AuthContext);

    const [children, setChildren] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingChild, setEditingChild] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [showAddParent, setShowAddParent] = useState(null);
    const [message, setMessage] = useState(null);

    // Add child form state
    const [newChild, setNewChild] = useState({
        firstName: '',
        lastName: '',
        birthDate: '',
        createLogin: false,
        email: '',
        password: '',
    });

    // Add parent form state
    const [parentEmail, setParentEmail] = useState('');

    const config = useCallback(() => ({
        headers: { Authorization: `Bearer ${user?.token}` }
    }), [user]);

    const fetchChildren = useCallback(async () => {
        if (!user?.token) return;
        try {
            const { data } = await axios.get(`${API_URL}/children`, config());
            setChildren(data);
        } catch (err) {
            console.error('Fehler beim Laden der Kinder:', err);
        } finally {
            setLoading(false);
        }
    }, [user, API_URL, config]);

    useEffect(() => {
        fetchChildren();
    }, [fetchChildren]);

    const showMessage = (text, isError = false) => {
        setMessage({ text, isError });
        setTimeout(() => setMessage(null), 5000);
    };

    const handleAddChild = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_URL}/children`, newChild, config());
            showMessage('Kind erfolgreich hinzugefügt!');
            setShowAddForm(false);
            setNewChild({ firstName: '', lastName: '', birthDate: '', createLogin: false, email: '', password: '' });
            fetchChildren();
        } catch (err) {
            showMessage(err.response?.data?.message || 'Fehler beim Hinzufügen.', true);
        }
    };

    const handleUpdateChild = async (childId, updates) => {
        try {
            await axios.put(`${API_URL}/children/${childId}`, updates, config());
            showMessage('Daten aktualisiert!');
            setEditingChild(null);
            fetchChildren();
        } catch (err) {
            showMessage(err.response?.data?.message || 'Fehler beim Aktualisieren.', true);
        }
    };

    const handleDeleteChild = async (childId) => {
        try {
            await axios.delete(`${API_URL}/children/${childId}`, config());
            showMessage('Kind erfolgreich entfernt.');
            setDeleteConfirm(null);
            fetchChildren();
        } catch (err) {
            showMessage(err.response?.data?.message || 'Fehler beim Löschen.', true);
        }
    };

    const handleAddParent = async (childId) => {
        try {
            await axios.post(`${API_URL}/children/${childId}/add-parent`, { parentEmail }, config());
            showMessage('Zweiter Elternteil erfolgreich verknüpft!');
            setShowAddParent(null);
            setParentEmail('');
        } catch (err) {
            showMessage(err.response?.data?.message || 'Fehler beim Verknüpfen.', true);
        }
    };

    const calculateAge = (birthDate) => {
        if (!birthDate) return null;
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        const m = today.getMonth() - birth.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
        return age;
    };

    if (loadingProfile) {
        return (
            <div className="flex items-center justify-center p-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!profile || profile.role !== 'eltern') {
        return (
            <div className="md3-card-elevated p-8 bg-error-container text-error-on-container m-6">
                <h2 className="text-headline-small font-bold mb-2">Zugriff verweigert</h2>
                <p>Diese Seite ist nur für Eltern zugänglich.</p>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4 max-w-5xl animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10">
                <div>
                    <h1 className="text-display-small font-bold text-surface-on mb-1">Meine Kinder</h1>
                    <p className="text-body-large text-surface-onVariant">
                        {children.length === 0 ? 'Noch keine Kinder registriert.' : `${children.length} ${children.length === 1 ? 'Kind' : 'Kinder'} registriert`}
                    </p>
                </div>
                <button
                    onClick={() => setShowAddForm(!showAddForm)}
                    className="md3-btn-filled gap-2 shadow-elevation-1 hover:shadow-elevation-2 shrink-0"
                >
                    {showAddForm ? <X style={{ fontSize: 18 }} /> : <UserPlus style={{ fontSize: 18 }} />}
                    {showAddForm ? 'Abbrechen' : 'Kind hinzufügen'}
                </button>
            </div>

            {/* Messages */}
            {message && (
                <div className={`mb-6 p-4 rounded-2xl flex items-center gap-3 shadow-sm animate-fade-in ${
                    message.isError
                        ? 'bg-error-container text-error-onContainer'
                        : 'bg-primary-container text-primary-onContainer'
                }`}>
                    {message.isError ? <AlertCircle style={{ fontSize: 20 }} /> : <CheckCircle style={{ fontSize: 20 }} />}
                    <span className="font-medium">{message.text}</span>
                </div>
            )}

            {/* Add Child Form */}
            {showAddForm && (
                <div className="md3-card-elevated p-8 mb-8 animate-scale-in">
                    <h3 className="text-title-large font-bold text-surface-on mb-6 pb-2 border-b border-outlineVariant flex items-center gap-3">
                        <UserPlus style={{ fontSize: 22 }} className="text-primary" />
                        Neues Kind anlegen
                    </h3>
                    <form onSubmit={handleAddChild} className="space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-label-large text-surface-onVariant mb-2 ml-1">Vorname *</label>
                                <input
                                    type="text"
                                    value={newChild.firstName}
                                    onChange={e => setNewChild({ ...newChild, firstName: e.target.value })}
                                    required
                                    className="md3-textfield-outlined"
                                    placeholder="Vorname"
                                />
                            </div>
                            <div>
                                <label className="block text-label-large text-surface-onVariant mb-2 ml-1">Nachname</label>
                                <input
                                    type="text"
                                    value={newChild.lastName}
                                    onChange={e => setNewChild({ ...newChild, lastName: e.target.value })}
                                    className="md3-textfield-outlined"
                                    placeholder={profile.userData?.lastName || 'Nachname'}
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-label-large text-surface-onVariant mb-2 ml-1">Geburtsdatum</label>
                            <input
                                type="date"
                                value={newChild.birthDate}
                                onChange={e => setNewChild({ ...newChild, birthDate: e.target.value })}
                                className="md3-textfield-outlined"
                            />
                        </div>

                        {/* Login toggle */}
                        <div className="p-4 bg-surface-containerLow rounded-xl border border-outlineVariant/50">
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={newChild.createLogin}
                                    onChange={e => setNewChild({ ...newChild, createLogin: e.target.checked })}
                                    className="w-5 h-5 rounded accent-primary"
                                />
                                <div>
                                    <span className="font-medium text-surface-on">Eigenen Login erstellen</span>
                                    <p className="text-body-small text-surface-onVariant mt-0.5">
                                        Damit kann sich dein Kind selbst im Dashboard anmelden und z.B. Blogbeiträge schreiben.
                                    </p>
                                </div>
                            </label>

                            {newChild.createLogin && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 animate-fade-in">
                                    <div>
                                        <label className="block text-label-large text-surface-onVariant mb-2 ml-1">E-Mail</label>
                                        <input
                                            type="email"
                                            value={newChild.email}
                                            onChange={e => setNewChild({ ...newChild, email: e.target.value })}
                                            className="md3-textfield-outlined"
                                            placeholder="kind@example.com"
                                            required={newChild.createLogin}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-label-large text-surface-onVariant mb-2 ml-1">Passwort</label>
                                        <input
                                            type="password"
                                            value={newChild.password}
                                            onChange={e => setNewChild({ ...newChild, password: e.target.value })}
                                            className="md3-textfield-outlined"
                                            placeholder="Mindestens 6 Zeichen"
                                            minLength={6}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end pt-2">
                            <button type="submit" className="md3-btn-filled gap-2">
                                <CheckCircle style={{ fontSize: 18 }} />
                                Kind anlegen
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Children List */}
            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
                </div>
            ) : children.length === 0 && !showAddForm ? (
                <div className="md3-card-elevated p-12 text-center">
                    <Users style={{ fontSize: 56 }} className="text-surface-onVariant/20 mx-auto mb-6" />
                    <h3 className="text-headline-small font-bold text-surface-on mb-2">Noch keine Kinder</h3>
                    <p className="text-body-large text-surface-onVariant mb-6">
                        Füge dein erstes Kind hinzu, um es für Events anmelden zu können.
                    </p>
                    <button
                        onClick={() => setShowAddForm(true)}
                        className="md3-btn-filled gap-2"
                    >
                        <UserPlus style={{ fontSize: 18 }} />
                        Kind hinzufügen
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {children.map(child => {
                        const age = calculateAge(child.userData?.birthDate);
                        const isEditing = editingChild === child._id;

                        return (
                            <div key={child._id} className="md3-card-elevated p-6 hover:shadow-elevation-2 transition-shadow">
                                {/* Child header */}
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-2xl bg-tertiary-container text-tertiary-onContainer flex items-center justify-center font-bold text-lg shadow-sm">
                                            {child.userData?.firstName?.[0]?.toUpperCase() || '?'}
                                        </div>
                                        <div>
                                            <h3 className="text-title-large font-bold text-surface-on">
                                                {child.userData?.firstName} {child.userData?.lastName}
                                            </h3>
                                            {age !== null && (
                                                <span className="text-body-medium text-surface-onVariant">{age} Jahre</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => setShowAddParent(showAddParent === child._id ? null : child._id)}
                                            className="md3-icon-btn text-surface-onVariant/60 hover:text-primary"
                                            title="Zweiten Elternteil einladen"
                                        >
                                            <UserPlus style={{ fontSize: 18 }} />
                                        </button>
                                        {deleteConfirm === child._id ? (
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={() => handleDeleteChild(child._id)}
                                                    className="md3-btn-filled !bg-error !text-error-on text-xs px-3 py-1"
                                                >
                                                    Löschen
                                                </button>
                                                <button
                                                    onClick={() => setDeleteConfirm(null)}
                                                    className="md3-btn-text text-xs"
                                                >
                                                    Nein
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => setDeleteConfirm(child._id)}
                                                className="md3-icon-btn text-error/40 hover:text-error"
                                            >
                                                <Trash style={{ fontSize: 18 }} />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Child details */}
                                <div className="space-y-2 text-body-medium text-surface-onVariant">
                                    {child.userData?.birthDate && (
                                        <div className="flex items-center gap-2">
                                            <Calendar style={{ fontSize: 16 }} className="opacity-60" />
                                            <span>{new Date(child.userData.birthDate).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                                        </div>
                                    )}
                                    {child.email && !child.email.endsWith('@child.local') && (
                                        <div className="flex items-center gap-2">
                                            <Mail style={{ fontSize: 16 }} className="opacity-60" />
                                            <span>{child.email}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Add second parent form */}
                                {showAddParent === child._id && (
                                    <div className="mt-4 p-4 bg-surface-containerLow rounded-xl border border-outlineVariant/50 animate-fade-in">
                                        <h4 className="text-label-large font-bold text-surface-on mb-3">Zweiten Elternteil einladen</h4>
                                        <div className="flex gap-3">
                                            <input
                                                type="email"
                                                value={parentEmail}
                                                onChange={e => setParentEmail(e.target.value)}
                                                className="md3-textfield-outlined flex-1"
                                                placeholder="E-Mail des zweiten Elternteils"
                                            />
                                            <button
                                                onClick={() => handleAddParent(child._id)}
                                                className="md3-btn-tonal shrink-0"
                                                disabled={!parentEmail}
                                            >
                                                Verknüpfen
                                            </button>
                                        </div>
                                        <p className="text-body-small text-surface-onVariant mt-2">
                                            Der zweite Elternteil muss bereits registriert sein und die Rolle „Eltern" haben.
                                        </p>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default MyChildren;
